import {Stack} from '../pixi_scene/game_scene/game_table/table_objects/stacks/Stack';
import {StackSelector} from './StackSelector';
import {ApiSendCommandsController} from './ApiSendCommandsController';
import {HotkeysControllerEvents} from './hotkeys_controller/HotkeysControllerEvents';
import {GameTableEvents} from '../pixi_scene/game_scene/game_table/GameTableEvents';
import {ZoomCommand} from '../enums/ZoomCommand';
import {DynamicData} from '../data/DynamicData';
import {ActiveDataCommandsController} from './ActiveDataCommandsController';
import {ActiveData} from '../data/ActiveData';
import EventApi from '../../../../api/EventsApi';
import {CardSelector} from './CardSelector';
import {Card} from '../pixi_scene/game_scene/game_table/table_objects/stacks/Card';
import {UnselectByMouseStoppedController} from './silent/UnselectByMouseStoppedController';
import {HandController} from './HandController';
import {DrawController} from './DrawController';

export class HotkeysController
{
    static paused: boolean = false;

    static start(): void {
        document.addEventListener('keyup', (keyboardEvent: KeyboardEvent) => {
            keyboardEvent.preventDefault();
            HotkeysController.onKeyup(keyboardEvent);
        });
    }

    static onKeyup(e: KeyboardEvent): void {
        if (HotkeysController.paused) {
            return;
        }
        if (!DynamicData.settings.canViewShortcuts) {
            return;
        }
        UnselectByMouseStoppedController.resetTimer();
        let hotkeyStacks: Stack[] = StackSelector.getHotkeyStacks();
        let hotkeyCards: Card[] = CardSelector.getHotkeyCards();
        if (hotkeyCards.length) {
            hotkeyStacks = [];
        }
        if (hotkeyStacks.length && !HandController.handHighLighted) {
            HotkeysController.processDraggingStacksHotkeys(e.code, hotkeyStacks);
            if (DynamicData.dragSomething) {
                return;
            }
            HotkeysController.processStacksHotkeys(e.code, hotkeyStacks);

        }
        if (hotkeyCards.length) {
            HotkeysController.processDraggingCardsHotkeys(e.code, hotkeyCards);
            if (DynamicData.dragSomething) {
                return;
            }
            HotkeysController.processCardsHotkeys(e.code, hotkeyCards);

        }
        HotkeysController.processTableHotkeys(e.code, e.ctrlKey);
    }

    private static processDraggingCardsHotkeys(keyCode: string, cards: Card[]): void {
        switch (keyCode) {
            case 'KeyF':
                if (!DynamicData.settings.canTableFlipCards) {
                    break;
                }
                if (DynamicData.dragSomething) {
                    if (cards[0].tempHoldingStack) {
                        cards.forEach(card => {
                            ActiveDataCommandsController.flipStackData(card.tempHoldingStack!.activeData);
                            ActiveData.dispatchEventsPool();
                        });
                    }
                    ApiSendCommandsController.flipCards(cards.map(card => card.sourceCard!));
                    EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'flip'});
                } else {
                    ApiSendCommandsController.flipCards(cards);
                }
                break;
            case 'KeyQ':
            case 'KeyE':
                if (!DynamicData.settings.canTableRotateCards) {
                    break;
                }
                let clockWise: boolean = keyCode == 'KeyE';
                if (DynamicData.dragSomething && cards[0].tempHoldingStack) {
                    cards.forEach(card => {
                        card.tempHoldingStack!.activeData.angle += (clockWise ? 15 : -15);
                        ActiveData.dispatchEventsPool();
                    });
                    EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'click'});
                }
                break;
        }
    }

    private static processCardsHotkeys(keyCode: string, cards: Card[]): void {
        switch (keyCode) {
            case 'Space':
                if (!DynamicData.settings.canTablePreviewCards) {
                    return;
                }
                DrawController.zoomCard(cards[cards.length - 1].activeData);
                CardSelector.unselectAll();
                break;
        }
    }

    private static processDraggingStacksHotkeys(keyCode: string, stacks: Stack[]): void {
        switch (keyCode) {
            case 'KeyF':
                if (!DynamicData.settings.canTableFlipCards) {
                    break;
                }
                if (DynamicData.dragSomething) {
                    stacks.forEach(stack => {
                        stack.dragFlipped = !stack.dragFlipped;
                        stack.activeData.cards.classes.forEach(cardData => cardData.flipped = !cardData.flipped);
                        ActiveData.hideEvents();
                        ActiveData.dispatchEventsPool();
                        ActiveData.showEvents();
                        ActiveDataCommandsController.flipStackData(stack.activeData);
                        ActiveData.dispatchEventsPool();
                    });
                    EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'flip'});
                } else {
                    ApiSendCommandsController.flipStacks(stacks);
                }
                break;
            case 'KeyQ':
            case 'KeyE':
                if (!DynamicData.settings.canTableRotateCards) {
                    break;
                }
                let clockWise: boolean = keyCode == 'KeyE';
                if (DynamicData.dragSomething) {
                    stacks.forEach(stack => {
                        stack.activeData.angle += (clockWise ? 15 : -15);
                        ActiveData.dispatchEventsPool();
                    });
                    EventApi.triggerEvent(EventApi.START_SOUND, {sound: 'click'});
                } else {
                    ApiSendCommandsController.rotateStacks(stacks, clockWise);
                }
                break;
        }
    }

    private static processStacksHotkeys(keyCode: string, stacks: Stack[]): void {
        switch (keyCode) {
            case 'KeyS':
                let manyCardsStacks: Stack[] = stacks.filter(stack => stack.activeData.cards.length > 1);
                if (!manyCardsStacks.length) {
                    break;
                }
                ApiSendCommandsController.shuffleStacks(stacks);
                StackSelector.unselectAll();
                break;
            case 'Delete':
                if (!DynamicData.settings.canTableDeleteCards) {
                    break;
                }
                ApiSendCommandsController.deleteStacks(stacks);
                StackSelector.unselectAll();
                break;
            case 'KeyL':
                if (!DynamicData.settings.canTableLockCards) {
                    break;
                }
                HotkeysController.toggleLockStacks(stacks);
                StackSelector.unselectAll();
                break;
            case 'Space':
                if (!DynamicData.settings.canTablePreviewCards) {
                    break;
                }
                let hoveredStack: Stack | null = StackSelector.hovered();
                if (hoveredStack) {
                    DrawController.zoomCard(hoveredStack.activeData.cards.getLast());
                    StackSelector.unselectAll();
                }
                break;
        }
    }

    private static toggleLockStacks(stacks: Stack[]): void {
        if (stacks.length > 1) {
            ApiSendCommandsController.lockStacks(stacks);
            return;
        }
        if (stacks[0].activeData.locked) {
            ApiSendCommandsController.unlockStacks(stacks);
        } else {
            ApiSendCommandsController.lockStacks(stacks);
        }
    }

    private static processTableHotkeys(keyCode: string, ctrlKey:boolean): void {
        switch (keyCode) {
            case 'Escape':
                DrawController.zoomed && DrawController.unzoom();
                dispatchEvent(new MessageEvent(HotkeysControllerEvents.ESCAPE, {data: {}}));
                StackSelector.unselectAll();
                CardSelector.unselectAll();
                break;
            case 'NumpadAdd':
                dispatchEvent(new MessageEvent(GameTableEvents.ZOOM, {data: {command: ZoomCommand.ZOOM_IN}}));
                break;
            case 'NumpadSubtract':
                dispatchEvent(new MessageEvent(GameTableEvents.ZOOM, {data: {command: ZoomCommand.ZOOM_OUT}}));
                break;
            case 'NumpadMultiply':
                dispatchEvent(new MessageEvent(GameTableEvents.ZOOM, {data: {command: ZoomCommand.ZOOM_RESET}}));
                break;
            case 'NumpadDivide':
                dispatchEvent(new MessageEvent(GameTableEvents.ZOOM, {data: {command: ZoomCommand.ZOOM_MIN}}));
                break;
            case 'Space':
                DrawController.zoomed && DrawController.unzoom();
                break;
            case 'KeyZ':
                if(ctrlKey && DrawController.zoomed){
                    DrawController.drawHolder.undo();
                }
                break;
        }
    }
}
